'use client';
// Mini 3D Q×V×Z scatter showing only the alpha-basket members at a selectable
// historical month. Mounted at the top of the Research Desk → Alpha Zone tab.
//
// Time slider:
//   - Far right: TODAY (live /api/stocks → full ScoredStock with factors,
//     so click on a planet opens the full StockDetail popup)
//   - Going back: monthly snapshots from the monthly-vintage cache (top-50
//     stocks per month-end, sparse data — Q/V/composite/sector/price only).
//     Click on a historical-only stock falls back to today's record where
//     possible; otherwise the popup opens with limited info.
//
// The monthly cache is generated by scripts/research/regen-monthly-vintage-cache.ts
// from the BBG bdh historical pull, served by /api/alpha-zone-monthly.

import { useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { ScatterPlot3D } from '@/components/ScatterPlot3D';
import { StockDetail } from '@/components/StockDetail';
import { PortfolioEventsFeed } from '@/components/PortfolioEventsFeed';
import type { ScoredStock, StockFactors } from '@/lib/compute';
import type { TechnicalStock } from '@/lib/hooks/use-visualizer-data';
import type { PillarScores } from '@/lib/types';

interface StocksResponse { stocks: ScoredStock[]; updatedAt?: string; }
interface BasketResponse {
  basket: {
    slots: Array<{ current: { symbol: string } }>;
    inception: string;
    lastRebalanceDate: string;
  };
  /** Filtered to BUY+ (composite ≥ 1.5). Used for display highlighting so
   *  decayed-HOLD slots don't appear as alpha-zone planets. */
  displaySlots?: Array<{ current: { symbol: string } }>;
}
interface MonthlyMonthsResponse { months: string[]; topPerMonth?: number; }
interface MonthlyStock {
  symbol: string;
  name?: string;
  sector: string;
  industry?: string;
  qualityScore: number;
  valuationScore: number;
  composite: number;
  price: number | null;
  marketCap?: number;
  priceMomentumZ?: number;
}
interface MonthlyMonthResponse { month: string; topPerMonth?: number; stocks: MonthlyStock[]; }

const EMPTY_FACTORS = {} as StockFactors;
const EMPTY_PILLARS: PillarScores = {
  profitability: null, growth: null, safety: null, shareholderReturns: null, momentum: null,
};

/** Convert a sparse monthly-cache row into a partial ScoredStock that
 *  ScatterPlot3D can render. Factor data is empty — the click→StockDetail
 *  will fall back to today's record by symbol when available. */
function monthlyToScoredStock(m: MonthlyStock): ScoredStock {
  return {
    symbol: m.symbol,
    name: m.name ?? m.symbol,
    sector: m.sector,
    industry: m.industry ?? m.sector,
    marketCap: m.marketCap ?? 0,       // sphere size; full-monthly cache provides historical mktCap
    price: m.price ?? 0,
    factors: EMPTY_FACTORS,
    pillarScores: EMPTY_PILLARS,
    qualityScore: m.qualityScore,
    valuationScore: m.valuationScore,
    momentumScore: m.priceMomentumZ ?? 0,
  };
}

/** Format YYYY-MM as e.g. "Apr 2024" */
function fmtMonth(m: string): string {
  const [y, mm] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mm,10)-1]} ${y}`;
}

export function AlphaZoneVisualizer() {
  const stocksResp = useResource<StocksResponse>('/api/stocks');
  const basketResp = useResource<BasketResponse>('/api/alpha-basket');
  // Use full-monthly cache (full universe with marketCap + priceMomentumZ) so the
  // alpha-zone slider's historical view shows real planet sizes + momentum spread.
  const monthsResp = useResource<MonthlyMonthsResponse>('/api/full-monthly');

  // months sorted ascending; index points to position in the array. The last
  // index represents "TODAY" (live data, not from the monthly cache).
  const months = useMemo(() => monthsResp.data?.months ?? [], [monthsResp.data]);
  const stops = useMemo(() => [...months, 'today'], [months]);

  // Default to "today" — last index in the stops array. Compute from stops
  // length rather than initializing to 0 + useEffect (which causes a flash
  // of the oldest month and triggers a lint warning).
  const [stopIdx, setStopIdx] = useState(() => Math.max(0, stops.length - 1));
  // When stops load (initial fetch), reset to today (the last stop).
  // Use a derived comparison via prev-prop pattern instead of useEffect+setState.
  const [stopsLength, setStopsLength] = useState(stops.length);
  if (stops.length !== stopsLength) {
    setStopsLength(stops.length);
    if (stops.length > 0) setStopIdx(stops.length - 1);
  }

  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  // Drop selection when slider position changes — derived state pattern
  const [prevStopIdx, setPrevStopIdx] = useState(stopIdx);
  if (stopIdx !== prevStopIdx) {
    setPrevStopIdx(stopIdx);
    if (selectedSymbol) setSelectedSymbol(null);
  }

  const isToday = stops[stopIdx] === 'today';
  const monthKey = isToday ? null : stops[stopIdx];

  // Fetch the monthly snapshot only when not on TODAY
  const monthlyResp = useResource<MonthlyMonthResponse>(
    monthKey ? `/api/full-monthly?month=${monthKey}` : null,
  );

  const allStocksToday: ScoredStock[] = useMemo(
    () => stocksResp.data?.stocks ?? [],
    [stocksResp.data],
  );

  // V mode toggle (Steve directive 2026-05-08): 'blended' = production
  // (0.75·peer-z + 0.25·universe-z absolute via valuationScoreBlended);
  // 'classic' = peer-z only. Toggle on the 3D scatter overlay (next to Top 15 Q).
  const [vMode, setVMode] = useState<'classic' | 'blended'>('blended');

  // Determine basket symbols and full ScoredStock array depending on mode
  const { alphaSymbols, plotStocks } = useMemo(() => {
    const applyV = (s: ScoredStock): ScoredStock => {
      if (vMode === 'classic') return s;
      const blended = s.valuationScoreBlended;
      return blended != null && Number.isFinite(blended) ? { ...s, valuationScore: blended } : s;
    };
    if (isToday) {
      const slots = basketResp.data?.displaySlots ?? basketResp.data?.basket?.slots ?? [];
      const syms = new Set(slots.map(s => s.current.symbol));
      const filtered = allStocksToday.filter(s => syms.has(s.symbol)).map(applyV);
      return { alphaSymbols: syms, plotStocks: filtered };
    }
    if (!monthlyResp.data) {
      return { alphaSymbols: new Set<string>(), plotStocks: [] };
    }
    // full-monthly cache returns the full universe — sort by composite desc and
    // take top-20 with Q ≥ 0 (the alpha-zone admission rule). Historical
    // snapshots predate V_blend; toggle is a no-op in retro view.
    const top20 = [...monthlyResp.data.stocks]
      .filter(s => Number.isFinite(s.qualityScore) && s.qualityScore >= 0 && Number.isFinite(s.composite))
      .sort((a, b) => b.composite - a.composite)
      .slice(0, 20);
    const syms = new Set(top20.map(s => s.symbol));
    const stocks = top20.map(monthlyToScoredStock);
    return { alphaSymbols: syms, plotStocks: stocks };
  }, [isToday, basketResp.data, allStocksToday, monthlyResp.data, vMode]);

  // Click → opens StockDetail. Use today's full record when available
  // (matches by symbol), so factor-detail works regardless of view date.
  const selectedStock = useMemo(() => {
    if (!selectedSymbol) return null;
    return allStocksToday.find(s => s.symbol === selectedSymbol) ?? null;
  }, [selectedSymbol, allStocksToday]);

  if (stocksResp.loading || basketResp.loading || monthsResp.loading) {
    return (
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-8 text-center text-[#64748b] text-sm">
        loading alpha-zone visualizer…
      </div>
    );
  }

  const stopLabel = isToday ? 'TODAY' : fmtMonth(monthKey!);
  const totalStops = stops.length;

  return (
    <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      {/* Header + date slider */}
      <div className="flex items-baseline justify-between px-4 py-3 border-b border-[#1e293b]">
        <div className="flex items-baseline gap-3">
          <span className="text-[11px] uppercase tracking-wide text-[#94a3b8] font-semibold">
            Alpha Zone Visualizer
          </span>
          <span className="text-[10px] text-[#64748b]">
            {plotStocks.length} of 20 planets
            {!isToday ? ` · click to see today's detail` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#94a3b8]">
          <span className="uppercase tracking-wider text-[#64748b]">View as of</span>
          <button
            onClick={() => setStopIdx(Math.max(0, stopIdx - 12))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Back 1 year"
          >‹‹</button>
          <button
            onClick={() => setStopIdx(Math.max(0, stopIdx - 1))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Back 1 month"
          >‹</button>
          <input
            type="range"
            min={0}
            max={totalStops - 1}
            value={stopIdx}
            onChange={e => setStopIdx(parseInt(e.target.value, 10))}
            className="w-[280px] accent-amber-400"
          />
          <button
            onClick={() => setStopIdx(Math.min(totalStops - 1, stopIdx + 1))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Forward 1 month"
          >›</button>
          <button
            onClick={() => setStopIdx(Math.min(totalStops - 1, stopIdx + 12))}
            className="text-[#64748b] hover:text-amber-300 px-1"
            title="Forward 1 year"
          >››</button>
          <button
            onClick={() => setStopIdx(totalStops - 1)}
            className="text-[10px] text-amber-300/70 hover:text-amber-300 uppercase tracking-wide font-mono"
          >Today</button>
          <span className="font-mono font-semibold text-amber-300 min-w-[5rem] text-right">{stopLabel}</span>
        </div>
      </div>

      {/* Month-tick labels: every January for orientation */}
      <div className="px-4 pt-1 pb-2 flex items-center justify-end gap-0.5 text-[8px] text-[#475569] font-mono overflow-x-auto">
        {stops.map((s, i) => {
          const isJan = s.endsWith('-01');
          const isCurrent = i === stopIdx;
          if (!isJan && !isCurrent && s !== 'today') return null;
          return (
            <button
              key={s + '-' + i}
              onClick={() => setStopIdx(i)}
              className={`px-1 cursor-pointer transition-colors hover:text-[#94a3b8] ${isCurrent ? 'text-amber-300' : ''}`}
            >
              {s === 'today' ? 'NOW' : s.slice(0, 4)}
            </button>
          );
        })}
      </div>

      {/* The mini 3D scatter — fixed height, only basket members rendered. */}
      <div className="relative" style={{ height: 480 }}>
        {plotStocks.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[#64748b] text-sm">
            {monthlyResp.loading ? 'loading snapshot…' : 'no data for this view'}
          </div>
        ) : (
          <>
            <div className="flex h-full">
              <ScatterPlot3D
                stocks={plotStocks}
                totalCount={plotStocks.length}
                technicalData={[] as TechnicalStock[]}
                technicalWeeklyData={[] as TechnicalStock[]}
                technicalMonthlyData={[] as TechnicalStock[]}
                highlightSymbol={''}
                onSelectStock={(sym) => setSelectedSymbol(sym)}
                selectedSymbol={selectedSymbol}
                alphaZoneStocks={alphaSymbols}
                hideEarningsCalendar
                legendPosition="bottom-right"
                vMode={vMode}
                onVModeChange={setVMode}
              />
            </div>
            {/* Bottom-left alerts overlay — same feed as Research Desk's Alpha
                Zone Alerts, scoped to today's basket symbols. Compact: ~280px
                wide, max-height ~210px so it doesn't crowd the 3D scene. */}
            {isToday && Array.from(alphaSymbols).length > 0 && (
              <div
                className="absolute left-3 bottom-3 z-20 w-[200px] bg-[#0d1117]/95 border border-[#1e293b] rounded-lg shadow-xl backdrop-blur-sm overflow-hidden"
                title="Recent BBG alerts scoped to current alpha basket"
              >
                <PortfolioEventsFeed
                  title="Alpha Zone Alerts"
                  symbols={Array.from(alphaSymbols)}
                  onSelectSymbol={(sym) => setSelectedSymbol(sym)}
                  compact
                  maxHeightPx={300}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* StockDetail popup overlay (uses today's record by symbol when scrubbing) */}
      {selectedStock && (
        <StockDetail
          stock={selectedStock}
          allStocks={allStocksToday}
          pinnedSymbols={new Set()}
          onTogglePin={() => {}}
          onClose={() => setSelectedSymbol(null)}
          isAlphaZone
        />
      )}
    </div>
  );
}
